import { WorkflowExecutionType } from "api/api.typing";
import Badge from "components/Badge";
import EmptyState from "components/EmptyState";
import Button from "components/buttons/Button";
import { Alert, AlertDescription } from "components/ui/alert";
import { Tabs, TabsContent, TabsTrigger } from "components/ui/tabs";
import { AlertTriangleIcon, FileBoxIcon, InfoIcon } from "lucide-react";
import { SideBar, SideBarPresence } from "pages/project/common/SideBar";
import { SideBarEntityTitle } from "pages/project/common/SideBarEntityTitle";
import { SideBarTabsList } from "pages/project/common/SideBarTabsList";
import { sideBarTabsTriggerClassName } from "pages/project/common/masterDetailLayout";
import { XTM_CAT_TOOL_ID, getCatToolName } from "pages/project/common/taskBase.typing";
import { XtmJobLinksTable } from "pages/project/common/taskBase/list/XtmJobLinksTable";
import { TaskBaseDetail } from "pages/project/common/taskBase/sidebar/TaskBaseDetail";
import TaskBaseFilesDownload from "pages/project/common/taskBase/sidebar/TaskBaseFilesDownload";
import RequestStatusBadge from "pages/project/request/components/sidebar/RequestStatusBadge";
import { RequestListType, RequestStatus } from "pages/project/request/request.typing";
import { UserHint } from "pages/project/task/components/sidebar/UserHint";
import { useValidationRequest } from "query/request.query";
import { ReactElement } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useValidationRequestFileStore } from "store/useRequestFileStore";
import { useShallow } from "zustand/react/shallow";

interface Props {
  selectedRequestId?: string;
  onClose: () => void;
  onErrorNode: () => ReactElement;
  onDecline: () => void;
  onAccept: () => void;
  filterType: RequestListType;
}

function RequestSideBar({ selectedRequestId, ...props }: Props) {
  const { t } = useTranslation();
  const { isLoading, validationRequest } = useValidationRequest(selectedRequestId);

  const state = useValidationRequestFileStore(
    useShallow((fileState) => ({
      shouldDisableAction: fileState.shouldDisableAction,
      requestZipFullDownload: fileState.requestZipFullDownload,
    }))
  );

  if (!isLoading && selectedRequestId && !validationRequest) {
    toast.error(
      <div className="ml-2 flex flex-col space-y-1">
        <span className="text-color-primary">{t("request.detail.error.title")}</span>
        <span className="text-color-secondary text-sm">{t("request.detail.error.description")}</span>
      </div>,
      {
        id: "no-request-id",
        className: "bg-color-primary",
      }
    );
    return props.onErrorNode();
  }

  const shouldDisableDownload = validationRequest ? state.shouldDisableAction(validationRequest.id) : false;
  const isRequestAccepted =
    !!validationRequest && [RequestStatus.Accepted, RequestStatus.AcceptedOnBehalf].includes(validationRequest.status);
  const isXtm =
    !!validationRequest &&
    validationRequest.catToolId === XTM_CAT_TOOL_ID &&
    validationRequest.workflowExecutionType === WorkflowExecutionType.CAT;

  return (
    <SideBarPresence present={!isLoading && !!validationRequest}>
      {validationRequest ? (
        <SideBar
          key="request-detail-panel"
          contentKey={validationRequest.id}
          onClose={props.onClose}
          title={
            <SideBarEntityTitle code={validationRequest.taskCode}>
              <Badge color="goldfish">{t("request.fields.request")}</Badge>
              <Badge>{validationRequest.taskType}</Badge>
              {getCatToolName(validationRequest.catToolId) && (
                <Badge dataTestId="task-type-value">{getCatToolName(validationRequest.catToolId)}</Badge>
              )}
              <RequestStatusBadge status={validationRequest.status} />
            </SideBarEntityTitle>
          }
        >
          {!isRequestAccepted && (
            <div className="mb-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Button color="ternary" label={t("request.detail.decline")} onClick={props.onDecline} />
                <Button color="primary" label={t("request.detail.accept")} onClick={props.onAccept} />
              </div>
              {validationRequest.acceptedByAnother && (
                <Alert variant="warning" className="mt-4">
                  <AlertTriangleIcon className="size-5" />
                  <AlertDescription>{t("request.detail.acceptedByAnother")}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <UserHint fileTabName={isXtm ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")} />

          <Tabs defaultValue="overview" className="-mx-6 mt-4 min-w-0 border-t border-gray-300 dark:border-t-gray-600">
            <SideBarTabsList>
              <TabsTrigger value="overview" data-test="overview-tab" className={sideBarTabsTriggerClassName}>
                <InfoIcon className="mr-2 size-4 shrink-0" />
                {t("translation:task.tabs.general")}
              </TabsTrigger>
              <TabsTrigger value="files" data-test="files-tab" className={sideBarTabsTriggerClassName}>
                <FileBoxIcon className="mr-2 size-4 shrink-0" />
                {isXtm ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")}
              </TabsTrigger>
            </SideBarTabsList>

            <TabsContent value="overview" className="px-6">
              <TaskBaseDetail
                selectedTaskBase={validationRequest}
                taskBaseStartDateLabel={t("task-base.detail.estimatedStartDate")}
                isUserSupplier={false}
                isCanEditSupplierContact={false}
              />
            </TabsContent>

            <TabsContent value="files" className="px-6">
              <div className="my-6">
                <TaskBaseFilesDownload
                  selectedTaskBase={validationRequest}
                  disableDownload={shouldDisableDownload}
                  onRequestZipFullDownload={(request, currentLanguage, hubConnectionId) =>
                    state.requestZipFullDownload(request.id, request, currentLanguage, hubConnectionId)
                  }
                />
              </div>

              {isXtm ? (
                <div className="my-4 border-t border-gray-200 px-2 pt-4 dark:border-slate-600">
                  <XtmJobLinksTable isTaskOngoing={false} taskId={validationRequest.taskId} />
                </div>
              ) : (
                <EmptyState
                  title={t("task.detail.delivery-files.title-no-files")}
                  subtitle={t("task.detail.delivery-files.title-no-files-detail")}
                />
              )}
            </TabsContent>
          </Tabs>
        </SideBar>
      ) : null}
    </SideBarPresence>
  );
}

export default RequestSideBar;
